import { useEffect, useState } from "react";
import SearchableSelect from "./SearchableSelect";
import { portalCall, money } from "./memberPortal/api";
import { switchPortal } from "./portalRouting";

const kinds = {
  dining: "Dining",
  subs: "Subscriptions",
  adhoc: "Ad hoc payments",
};
export default function CrapAppSettings({ styles: s = {} }) {
  const [lodge, setLodge] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    portalCall("Administration", { lodgeNumber: lodge })
      .then((value) => {
        if (active) setData(value);
      })
      .catch((e) => {
        if (active) setError(e.message || "Could not load settings.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [lodge, refresh]);
  const details = !loading && !error ? data?.details : null;
  return (
    <section>
      <h3>
        Crap App administration <small>Beta</small>
      </h3>
      <p>
        Manage each lodge’s platform fees and check service status. Detailed
        member information is available through logged support access.
      </p>
      {data && (
        <p>
          Payment environment:{" "}
          <strong>{data.mode === "live" ? "Live" : "Test"}</strong>
        </p>
      )}
      <label>
        Lodge
        <SearchableSelect
          value={lodge}
          onChange={(e) => setLodge(e.target.value)}
          style={s.inp}
        >
          <option value="">Choose a lodge</option>
          {(data?.lodges || []).map((l) => (
            <option key={l.id} value={l.id}>
              {l.name} (#{l.id})
            </option>
          ))}
        </SearchableSelect>
      </label>
      <p>
        <button
          style={s.btn2}
          disabled={loading}
          onClick={() => setRefresh((v) => v + 1)}
        >
          Refresh status
        </button>{" "}
        <button style={s.btn2} onClick={() => switchPortal("members")}>
          Open Crap App support
        </button>
      </p>
      <p>
        In Crap App, choose the lodge and enter the reason for access to open
        its treasurer controls. Use the main system’s Audit Log to search
        administrative actions.
      </p>
      {loading && <p role="status">Loading settings…</p>}
      {error && <p role="alert">{error}</p>}
      {details && (
        <>
          <h4>Lodge access and Stripe</h4>
          <p>
            {Object.entries(details.membershipCounts)
              .map(([role, count]) => `${role}: ${count}`)
              .join(" · ") || "No portal memberships yet."}
          </p>
          <p>
            {details.account.connected
              ? `Stripe account: ${details.account.accountId} (${details.account.mode})`
              : "Stripe account not connected."}
            {details.account.connected &&
              details.account.mode !== data.mode &&
              " This account must be reconnected in the current payment environment."}
          </p>
          <p>
            This shows the stored connection; it does not verify Stripe’s
            current payment or payout restrictions. Treasurers and Stewards
            manage lodge memberships in Crap App. Main-system staff access is
            managed on Users.
          </p>
          <Commission
            key={`${lodge}:${details.policy?.revision || 0}:${refresh}`}
            lodge={lodge}
            policy={details.policy}
            styles={s}
            done={() => setRefresh((v) => v + 1)}
          />
          <h4>Payment and notification troubleshooting</h4>
          {details.truncated && (
            <p role="alert">
              Summary limited to 500 records per category. Use the lodge support
              view for further investigation.
            </p>
          )}
          <Troubleshooting
            title="Outstanding payment requests"
            rows={details.payments}
            render={(r) =>
              `${r.status || "Unknown"} — ${money(r.amountPence)} total; ${money(r.receivedPence)} received`
            }
            advice="Unpaid requests may simply be awaiting the payer. For failed or attention-required payments, inspect the lodge’s Payments page and Stripe before retrying. Do not record a manual receipt for a Stripe payment."
          />
          <Troubleshooting
            title="Pending subscription changes"
            rows={details.subscriptionChanges}
            render={(r) => r.status}
            advice="Cancellation reconciliation runs automatically. Check the subscription in the lodge support view if it remains pending; a collection may already be in flight."
          />
          <Troubleshooting
            title="Notification delivery queue"
            rows={details.notifications}
            render={(r) =>
              `${r.status} — ${r.attempts} attempts${r.emailSent ? "; email already sent" : ""}`
            }
            advice="Pending/retry deliveries are handled automatically. For failed deliveries, check the approved recipient list in staging and mail configuration. The lodge treasurer or Steward can retry failed deliveries from their notifications administration view."
          />
        </>
      )}
    </section>
  );
}
function Troubleshooting({ title, rows, render, advice }) {
  return (
    <details style={{ marginBottom: 16 }}>
      <summary>
        {title} ({rows.length})
      </summary>
      <p>{advice}</p>
      {!rows.length ? (
        <p>No outstanding items found.</p>
      ) : (
        <ul>
          {rows.map((r) => (
            <li
              key={r.id}
              style={{ overflowWrap: "anywhere", marginBottom: 8 }}
            >
              <code>{r.id}</code>: {render(r)}
            </li>
          ))}
        </ul>
      )}
    </details>
  );
}
function Commission({ lodge, policy, styles: s, done }) {
  const [enabled, setEnabled] = useState(!!policy?.types?.length);
  const [percent, setPercent] = useState((policy?.bps || 0) / 100);
  const [fixed, setFixed] = useState((policy?.fixedPence || 0) / 100);
  const [types, setTypes] = useState(
    policy?.types?.length ? policy.types : Object.keys(kinds),
  );
  const [refund, setRefund] = useState(
    policy?.refundCommission || "proportional",
  );
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [saved, setSaved] = useState(false);
  const fee = Math.min(
    10000,
    Math.round((10000 * Number(percent)) / 100) +
      Math.round(Number(fixed) * 100),
  );
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        setSaved(false);
        try {
          if (enabled && !types.length)
            throw Error(
              "Choose at least one payment type or disable commission.",
            );
          await portalCall("Commission", {
            lodgeNumber: lodge,
            bps: Math.round(Number(percent) * 100),
            fixedPence: Math.round(Number(fixed) * 100),
            types: enabled ? types : [],
            refundCommission: refund,
            revision: policy?.revision || 0,
          });
          setSaved(true);
        } catch (e) {
          setError(e.message || "Could not save commission.");
        } finally {
          setBusy(false);
        }
      }}
    >
      <h4>Platform commission</h4>
      <p>
        Fees are deducted from the lodge’s proceeds. Members pay the advertised
        amount. Stripe processing fees are separate. Changes apply to new
        payment requests; existing requests retain their recorded commission.
      </p>
      <fieldset disabled={busy || saved} style={{ border: 0, padding: 0 }}>
        <label>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />{" "}
          Enable platform commission for this lodge
        </label>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
            gap: 12,
            margin: "16px 0",
          }}
        >
          <label>
            Percentage (%) *
            <input
              style={s.inp}
              type="number"
              required
              min="0"
              max="100"
              step="0.01"
              value={percent}
              onChange={(e) => setPercent(e.target.value)}
            />
          </label>
          <label>
            Fixed fee per payment (£) *
            <input
              style={s.inp}
              type="number"
              required
              min="0"
              max="1000"
              step="0.01"
              value={fixed}
              onChange={(e) => setFixed(e.target.value)}
            />
          </label>
        </div>
        <p>Apply to:</p>
        {Object.entries(kinds).map(([key, label]) => (
          <label key={key} style={{ display: "block", marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={types.includes(key)}
              onChange={(e) =>
                setTypes(
                  e.target.checked
                    ? [...types, key]
                    : types.filter((t) => t !== key),
                )
              }
            />
            {label}
          </label>
        ))}
        <p>
          {enabled
            ? `For a £100 payment of a selected type: ${money(fee)} platform fee; ${money(10000 - fee)} to the lodge before Stripe fees.`
            : "Platform commission is off for new payment requests."}
        </p>
        <label>
          Commission refund policy
          <select
            style={s.inp}
            value={refund}
            onChange={(e) => setRefund(e.target.value)}
          >
            <option value="proportional">
              Refund commission proportionately
            </option>
            <option value="retain">Retain commission</option>
          </select>
        </label>
        <p>This refund setting also applies to refunds of existing payments.</p>
        <button style={s.btn} type="submit">
          {busy ? "Saving…" : "Save commission settings"}
        </button>
      </fieldset>
      {error && <p role="alert">{error}</p>}
      {saved && (
        <p role="status">
          Commission settings saved.{" "}
          <button type="button" onClick={done}>
            Reload settings
          </button>
        </p>
      )}
    </form>
  );
}
